import React from 'react';
import type { ThemePalette, TypographyTokens } from '../../theme/tokens';

/**
 * Decorative floating-ball illustration shared by OnboardingPage's hero and
 * HomePage's empty-state card. Marked aria-hidden at the root — this is pure
 * decoration and must never be announced by screen readers.
 *
 * The @keyframes floaty rule is declared once here via a shared CSS class so
 * multiple instances on the same page don't re-emit the same keyframe block.
 */

export interface FloatingBallsProps {
  c: ThemePalette;
  typ: TypographyTokens;
  /** 'lg' for full onboarding hero, 'md' for mid-size slots, 'sm' for inline card accents. */
  size?: 'sm' | 'md' | 'lg';
  /** Optional label rendered in the center ball (e.g. "7"). Omit for satellites-only. */
  centerLabel?: string;
  className?: string;
  style?: React.CSSProperties;
}

interface SatelliteSpec {
  top?: string;
  left?: string;
  right?: string;
  bottom?: string;
  n: number;
  /** Size multiplier relative to the scale base. */
  sizeFactor: number;
  bgKey: 'ink' | 'accent2' | 'surface' | 'lucky' | 'accent3';
  bordered?: boolean;
}

const SATELLITES: SatelliteSpec[] = [
  { top: '10%', left: '18%', n: 23, sizeFactor: 0.37, bgKey: 'ink' },
  { top: '18%', right: '15%', n: 14, sizeFactor: 0.31, bgKey: 'accent2' },
  { bottom: '18%', left: '10%', n: 42, sizeFactor: 0.34, bgKey: 'surface', bordered: true },
  { bottom: '12%', right: '20%', n: 31, sizeFactor: 0.4, bgKey: 'lucky' },
  { top: '38%', right: '4%', n: 9, sizeFactor: 0.26, bgKey: 'accent3' },
];

const SIZE_MAP: Record<NonNullable<FloatingBallsProps['size']>, {
  container: number;
  center: number;
  scaleBase: number;
}> = {
  sm: { container: 130, center: 54, scaleBase: 110 },
  md: { container: 190, center: 92, scaleBase: 160 },
  lg: { container: 260, center: 140, scaleBase: 220 },
};

function resolveBg(key: SatelliteSpec['bgKey'], c: ThemePalette): string {
  switch (key) {
    case 'ink':
      return c.ink;
    case 'accent2':
      return c.accent2;
    case 'surface':
      return c.surface;
    case 'lucky':
      return c.lucky;
    case 'accent3':
      return c.accent3;
  }
}

function resolveFg(key: SatelliteSpec['bgKey'], c: ThemePalette): string {
  switch (key) {
    case 'ink':
      return c.bg;
    case 'accent2':
      return c.ink;
    case 'surface':
      return c.ink;
    case 'lucky':
      return '#fff';
    case 'accent3':
      return '#fff';
  }
}

export const FloatingBalls: React.FC<FloatingBallsProps> = ({
  c,
  typ,
  size = 'lg',
  centerLabel,
  className,
  style,
}) => {
  const dims = SIZE_MAP[size];

  return (
    <div
      aria-hidden="true"
      className={`relative flex shrink-0 items-center justify-center ${className ?? ''}`}
      style={{
        width: dims.container,
        height: dims.container,
        ...style,
      }}
    >
      <style>{`
        @keyframes chancey-floaty {
          from { transform: translateY(-6px) rotate(-3deg); }
          to { transform: translateY(6px) rotate(3deg); }
        }
      `}</style>
      {centerLabel !== undefined && (
        <div
          className="chancey-decor-glow relative flex items-center justify-center rounded-full text-white shadow-2xl"
          style={{
            width: dims.center,
            height: dims.center,
            fontSize: dims.center * 0.51,
            background: c.gradient,
            fontFamily: typ.display,
            fontWeight: typ.displayWeight,
            letterSpacing: '-0.03em',
            boxShadow: `0 ${dims.center * 0.21}px ${dims.center * 0.43}px ${c.accent}40, inset 0 -${dims.center * 0.11}px ${dims.center * 0.21}px rgba(0,0,0,0.2)`,
          }}
        >
          {centerLabel}
          <div
            className="pointer-events-none absolute inset-0 rounded-full"
            style={{
              background:
                'radial-gradient(circle at 30% 25%, rgba(255,255,255,0.4) 0%, transparent 55%)',
            }}
          />
        </div>
      )}
      {SATELLITES.map((b, i) => {
        const ballSize = Math.round(dims.scaleBase * b.sizeFactor);
        return (
          <div
            key={i}
            className="chancey-decor-glow chancey-decor-motion absolute flex items-center justify-center rounded-full font-mono font-bold shadow-lg"
            style={{
              top: b.top,
              left: b.left,
              right: b.right,
              bottom: b.bottom,
              width: ballSize,
              height: ballSize,
              borderRadius: ballSize / 2,
              background: resolveBg(b.bgKey, c),
              color: resolveFg(b.bgKey, c),
              fontSize: ballSize * 0.38,
              border: b.bordered ? `1px solid ${c.line}` : 'none',
              animation: `chancey-floaty ${3 + i * 0.4}s ease-in-out ${i * 0.2}s infinite alternate`,
              boxShadow: `inset 0 -${ballSize * 0.08}px ${ballSize * 0.15}px rgba(0,0,0,0.2), 0 6px 14px rgba(0,0,0,0.12)`,
              fontFamily: typ.mono,
            }}
          >
            {b.n}
          </div>
        );
      })}
    </div>
  );
};
